import { Link } from "react-router";
import { useGetRecordQuery, useGetUserQuery } from "@/api/api";
import { useBreakpoint } from "@/common/hooks";
import { useAppSelector } from "@/state/redux/hooks";
import { selectMe } from "@/state/redux/slices/ui";
import { HeaderText } from "./Header";
import UserAvatar from "./UserAvatar";

/**
 * Whose record this is, and the way into their space — the record page's
 * stand-in for the collections control, which has no panel to open there.
 *
 * The owner, not the reader: a record reached from someone else's space names
 * them, so the one identity on screen is always the one the link goes to.
 *
 * The readable URL spells the owner out in its first segment. The legacy
 * permalink does not, so the record itself is asked — the same query
 * `RecordPage` already ran for the body below, hitting its cache rather than
 * the network. `me` short-circuits the profile lookup on one's own records,
 * which is most of them.
 */
export default function RecordOwnerWidget({
  userRef,
  recordID,
}: {
  userRef?: string;
  recordID?: string;
}) {
  const { isMd } = useBreakpoint();
  const me = useAppSelector(selectMe);
  const { data: record } = useGetRecordQuery(recordID ?? "", {
    skip: !recordID,
  });

  const ref = userRef || record?.owner_nickname || record?.user_id;
  const isMe =
    !!me && !!ref && (ref === me.id || ref === me.nickname?.nickname);

  const { data: owner } = useGetUserQuery(ref ?? "", { skip: !ref || isMe });

  const name = isMe
    ? me.name || me.nickname?.nickname || me.id
    : owner?.name || owner?.nickname;
  const picture = isMe ? me.picture : owner?.picture;

  // Nothing to name yet: the profile lookup is still out, or the record does
  // not resolve at all. The page below says so, and a "?" avatar standing in
  // for nobody would only add noise.
  if (!ref || !(name || picture)) return null;

  const to = isMe ? `/${me.nickname?.nickname || me.id}` : `/${ref}`;

  return (
    <Link
      to={to}
      title={name ? `${name}'s collections` : "Collections"}
      // `min-w-0` and the truncation inside it: a long name gives way before it
      // pushes the brand off the other end.
      className="flex min-w-0 shrink items-center gap-2 rounded-md px-2 py-1 hover:bg-accent"
    >
      {/* On mobile the avatar stands in for the name, as it does elsewhere. */}
      {isMd && name && <HeaderText>{name}</HeaderText>}
      <UserAvatar name={name} picture={picture} className="size-5 shrink-0" />
    </Link>
  );
}
